import React, {useEffect, useState, useMemo} from 'react';
import * as PropTypes from 'prop-types';
import {InputContainerStyle, InputLabelStyle, InputStyle} from './input.styled';
import {convertBasicValue} from './input-logic';

export const BasicNumberInput = ({
	innerRef,
	maxLength,
	label,
	modalView,
	taskModalStyle,
	value,
	placeholder,
	disabled,
	hasError,
	onClick,
	onBlur,
	onChange,
	filterInput,
	parentKeyDown,
	callback,
	converterValue,
	numberSymbol,
	specialRule,
	showAsterisk,
	width,
	cy,
	alignLeft,
	forceUpdate,
	filterNonNumerics,
	autoFocus,
	preventNumberConversion,
	className,
	noBorder,
	noLocked,
}) => {
	const formattedValue = useMemo(
		() => converterValue(value, specialRule, showAsterisk, preventNumberConversion),
		[value, numberSymbol]
	);
	const [currentInputValue, setCurrentInputValue] = useState(formattedValue.full);
	const [error, setError] = useState(formattedValue.error);

	// Need to find a better name, but this is internal logic only
	const [dirtyWithError, setDirtyWithError] = useState(false);
	// Will only be true if user has entered something else inside the input
	const [isDirty, setIsDirty] = useState(false);

	const setErrorIfDirty = () => {
		if (dirtyWithError) {
			setError(true);
		}
	};

	useEffect(() => {
		setDirtyWithError(true);
		const formattedValue = converterValue(value, specialRule, showAsterisk, preventNumberConversion);
		setCurrentInputValue(formattedValue.full);
		if (formattedValue.error !== error) {
			setError(formattedValue.error);
		}
	}, [value, forceUpdate, numberSymbol]);

	const filterCharacters = value => {
		// Filter characters except 'e' which is a valid symbol for exponent in numbers
		return String(value).replace(/([a-df-zA-DF-Z])/g, '');
	};

	const handleOnChange = e => {
		const rawValue = filterNonNumerics ? filterCharacters(e.target.value) : e.target.value;
		const newValue = filterInput ? filterInput(rawValue) : rawValue;
		if (filterInput && newValue === '' && rawValue !== '') {
			e.target.value = currentInputValue;
			return;
		}
		setIsDirty(true);
		setCurrentInputValue(newValue);
		if (onChange) {
			onChange(newValue);
		}
	};

	const convertValue = value => {
		return converterValue(value, specialRule, showAsterisk, preventNumberConversion);
	};

	const handleValidate = e => {
		const inputValue = e.target.value;

		const validValue = convertValue(inputValue);

		if (validValue.error) {
			setErrorIfDirty();
		} else {
			setError(false);
			setCurrentInputValue(validValue.full);
		}
		if (callback) {
			// else we just call the mutation;
			callback(validValue.float, e, isDirty);
			setIsDirty(false);
		}
		if (onBlur) {
			onBlur(e, validValue.float);
		}
	};

	const handleKeyPressInput = e => {
		if (e.key === 'Enter') {
			e.target.blur();
		}

		parentKeyDown && parentKeyDown(e, convertValue(currentInputValue));
	};

	const handleOnClick = e => {
		if (onClick) {
			onClick(e.target);
		}
	};

	const handleOnFocus = e => {
		e.target.select();
	};

	const inputState = disabled ? '' : error || hasError ? ' error' : '';
	return (
		<InputContainerStyle modalView={modalView} taskModalStyle={taskModalStyle}>
			{label ? <InputLabelStyle>{label}</InputLabelStyle> : null}
			<InputStyle
				ref={innerRef}
				modalView={modalView}
				taskModalStyle={taskModalStyle}
				className={className + ' ' + inputState || ''}
				type={'text'}
				placeholder={placeholder}
				value={currentInputValue}
				onFocus={e => handleOnFocus(e)}
				onClick={e => handleOnClick(e)}
				onChange={e => handleOnChange(e)}
				onBlur={e => handleValidate(e)}
				onKeyDown={e => handleKeyPressInput(e)}
				disabled={disabled}
				width={width}
				data-cy={cy}
				alignLeft={alignLeft}
				maxLength={maxLength}
				autoFocus={autoFocus}
				noBorder={noBorder}
				noLocked={noLocked}
			></InputStyle>
		</InputContainerStyle>
	);
};

BasicNumberInput.propTypes = {
	/**
	 * Ref to the input
	 */
	innerRef: PropTypes.object,

	/**
	 * Information when no value has been set yet.
	 */
	placeholder: PropTypes.string,

	/**
	 * Label for input
	 */
	label: PropTypes.string,
	/**
	 * Disable the input the user can't insert a new value or edit the current one.
	 */
	disabled: PropTypes.bool,
	/**
	 * Don't show border in invoice entry number fields
	 */
	noBorder: PropTypes.bool,
	/**
	 * Don't lock invoice entry number fields
	 */
	noLocked: PropTypes.bool,
	/**
	 * When the input is focused the current value is selected. And the cursor ready to receive text input
	 */
	onClick: PropTypes.func,
	/**
	 * On blue the input parse the value to change it to the hours format (1h 30m).
	 * If this succed then it calls the mutation from the parent, else it keep the value typed and set the input as invalid
	 */
	onBlur: PropTypes.func,
	/**
	 * Called on every keystroke with the raw string value
	 */
	onChange: PropTypes.func,
	/**
	 * Optional function to filter/transform the value on each keystroke before it is displayed
	 */
	filterInput: PropTypes.func,
	/**
	 * Indicator to show that the input is in an invalid state
	 */
	hasError: PropTypes.bool,
	/**
	 * Action to execute if the validation is successful
	 */
	callback: PropTypes.func,
	/**
	 * Value of the input
	 */
	value: PropTypes.any,
	/**
	 *
	 */
	converterValue: PropTypes.func.isRequired,

	specialRule: PropTypes.func,

	showAsterisk: PropTypes.bool,

	/**
	 * Specific styling for the scheduling modals
	 */
	modalView: PropTypes.bool,

	/**
	 * Specific styling for the input in the task modal
	 */
	taskModalStyle: PropTypes.bool,

	/**
	 * Width of the input
	 */
	width: PropTypes.string,

	cy: PropTypes.string,
	alignLeft: PropTypes.bool,

	maxLength: PropTypes.number,

	/**
	 * Flag to force the input to re render
	 */
	forceUpdate: PropTypes.any,

	intl: PropTypes.shape({
		formatNumber: PropTypes.func,
	}),
	/**
	 * callback when pressing key, returning event and current value
	 */
	parentKeyDown: PropTypes.func,

	/**
	 * additional symbol, for example currency
	 */
	numberSymbol: PropTypes.string,

	/**
	 * filter non numeric characters from input
	 */
	filterNonNumerics: PropTypes.bool,

	/**
	 * autoFocus the input
	 */
	autoFocus: PropTypes.bool,

	/**
	 * Prevent converting text to number to keep trailing decimals
	 */
	preventNumberConversion: PropTypes.bool,

	className: PropTypes.string,
};

BasicNumberInput.defaultProps = {
	disabled: false,
	hasError: false,
	specialRule: val => val,
	converterValue: (val, specialRule, showAsterisk, preventNumberConversion) =>
		convertBasicValue(val, specialRule, showAsterisk, preventNumberConversion),
	intl: {
		formatNumber: ({value}) => value,
	},
};
